import { ChangeEvent } from "react"
import { useTranslation } from "react-i18next"

import UploadIcon from "@/assets/icons/upload.svg?react"
import { Button } from "@/components/ds/Button"
import { Typography } from "@/components/ds/Typography"
import { DragDropZone } from "@/components/shared/DragDropZone"
import { useDragAndDrop } from "@/hooks/useDragAndDrop"
import { useFileUpload } from "@/hooks/useUploadFile"
import { cn } from "@/lib/utils"

interface ImageUploadZoneProps {
  value: string
  onChange: (url: string) => void
  alt: string
  className?: string
}

export function ImageUploadZone({ value, onChange, alt, className }: ImageUploadZoneProps) {
  const { t } = useTranslation()

  const {
    handleFileUpload,
    isUploading: isUploadingFile,
    inputRefBase,
  } = useFileUpload({
    onChange: (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    value,
  })

  const {
    isDragOver,
    isUploading: isDragUploading,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    onFileSelect: onChange,
    acceptedTypes: ["image/"],
  })

  const isUploading = isUploadingFile || isDragUploading

  if (value) {
    return (
      <div
        className={cn(
          "relative h-[200px] w-[200px] shrink-0 overflow-hidden rounded-xl border border-neutral-border",
          className,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <img src={value} alt={alt} className="h-full w-full object-cover" />
        <button
          type="button"
          onClick={() => {
            onChange("")
            if (inputRefBase.current) inputRefBase.current.value = ""
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity hover:opacity-100"
        >
          <Typography variant="body2" className="font-medium text-white">
            {t("imageUpload.change")}
          </Typography>
        </button>
        <input ref={inputRefBase} type="file" accept="image/*" className="sr-only" onChange={handleFileUpload} />
      </div>
    )
  }

  return (
    <DragDropZone
      isDragOver={isDragOver}
      isUploading={isUploading}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      defaultText={t("imageUpload.dropFilesOr")}
      uploadingText={t("imageUpload.uploading")}
      className={cn("h-[200px] w-[200px] shrink-0 rounded-xl bg-white !p-4", className)}
    >
      <Button
        type="button"
        variant="secondary"
        size="small"
        disabled={isUploading}
        onClick={() => inputRefBase.current?.click()}
      >
        <UploadIcon className="w-5" />
        {t("selectFile")}
      </Button>
      <input ref={inputRefBase} type="file" accept="image/*" className="sr-only" onChange={handleFileUpload} />
    </DragDropZone>
  )
}
